class Cart {
  constructor() {
    // Initialize cart elements
    this.cart = document.querySelector('.side-cart');
    this.overlay = document.querySelector('.cart-overlay');
    this.toggleButtons = document.querySelectorAll('.cart-toggle');
    this.loadingOverlay = this.cart.querySelector('.cart-loading-overlay');

    if (!this.cart || !this.overlay) {
      console.error('Required cart elements not found');
      return;
    }

    // Create a new loading overlay specifically for cart updates
    this.cartUpdateOverlay = document.createElement('div');
    this.cartUpdateOverlay.classList.add('cart-update-overlay', 'hidden');
    this.cartUpdateOverlay.innerHTML = `
      <div class="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-[400]">
        <div class="loading-spinner">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    `;
    this.cart.appendChild(this.cartUpdateOverlay);

    // Bind event listeners
    this.bindEvents();
  }

  bindEvents() {
    // Cart toggle - using event delegation for dynamically added elements
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cart-toggle')) {
        e.preventDefault();
        this.openCart();
      }
    });

    // Close cart
    document.querySelector('.close-cart').addEventListener('click', () => this.closeCart());
    this.overlay.addEventListener('click', () => this.closeCart());

    // Quantity changes
    this.cart.addEventListener('click', (e) => {
      if (e.target.matches('.quantity-increase')) {
        const input = e.target.previousElementSibling;
        input.value = parseInt(input.value) + 1;
        this.updateQuantity(input);
      }
      if (e.target.matches('.quantity-decrease')) {
        const input = e.target.nextElementSibling;
        input.value = Math.max(0, parseInt(input.value) - 1);
        this.updateQuantity(input);
      }
    });

    // Remove items
    this.cart.addEventListener('click', (e) => {
      const removeButton = e.target.closest('.remove-item');
      if (removeButton) {
        const lineItem = removeButton.closest('.cart-item');
        const key = lineItem.dataset.lineItemKey;
        this.removeItem(key);
      }
    });

    // Listen for quantity input changes
    this.cart.addEventListener('change', (e) => {
      if (e.target.matches('input[name="quantity"]')) {
        this.updateQuantity(e.target);
      }
    });
  }

  openCart() {
    this.cart.classList.remove('translate-x-full');
    this.overlay.classList.remove('hidden');
    this.overlay.classList.remove('opacity-0');
    document.body.style.overflow = 'hidden';
  }

  closeCart() {
    this.cart.classList.add('translate-x-full');
    this.overlay.classList.add('opacity-0');
    setTimeout(() => {
      this.overlay.classList.add('hidden');
    }, 300);
    document.body.style.overflow = '';
  }

  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
    }
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add('hidden');
    }
  }

  showUpdateOverlay() {
    this.cartUpdateOverlay.classList.remove('hidden');
  }

  hideUpdateOverlay() {
    this.cartUpdateOverlay.classList.add('hidden');
  }

  async updateQuantity(input) {
    const lineItem = input.closest('.cart-item');
    const key = lineItem.dataset.lineItemKey;
    const quantity = parseInt(input.value);

    // If quantity is 0, remove the item
    if (quantity === 0) {
      await this.removeItem(key);
      return;
    }

    try {
      this.showUpdateOverlay();
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      const cart = await response.json();
      this.updateCart(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      this.hideUpdateOverlay();
    }
  }

  async removeItem(key) {
    try {
      this.showUpdateOverlay();
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      const cart = await response.json();

      // Find and remove the line item from DOM
      const lineItem = this.cart.querySelector(`[data-line-item-key="${key}"]`);
      if (lineItem) {
        // Remove the line item and its border
        const nextBorder = lineItem.nextElementSibling;
        if (nextBorder && nextBorder.classList.contains('border-b')) {
          nextBorder.remove();
        }
        lineItem.remove();
      }

      this.updateCart(cart);
      await updateCartCount();
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      this.hideUpdateOverlay();
    }
  }

  async fetchAndUpdateCart() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      if (!response.ok) throw new Error('Network response was not ok');
      const cart = await response.json();
      this.updateCart(cart);
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  updateCart(cart) {
    // Update subtotal
    const subtotal = this.cart.querySelector('.cart-subtotal');
    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cart.total_price/100);

    console.log('Cart Total Price:', cart.total_price);
    console.log('Formatted Cart Price:', formattedPrice);

    subtotal.textContent = formattedPrice;

    // Get the cart items container
    const cartItemsContainer = this.cart.querySelector('.cart-items');
    if (!cartItemsContainer) return;

    // Clear existing items
    cartItemsContainer.innerHTML = '';

    // Render each cart item
    cart.items.forEach((item, index) => {
      const itemPrice = (item.final_price/100);
      const formattedItemPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(itemPrice);

      console.log('Item Price:', itemPrice);
      console.log('Formatted Item Price:', formattedItemPrice);

      const itemHTML = `
        <div class="cart-item grid grid-cols-6 gap-x-5 py-[8px]" data-line-item-key="${item.key}">
          <div class="w-[100px]">
            <img src="${item.image}" alt="${item.title}" class="aspect-square">
          </div>
          <div class="flex flex-col justify-between flex-1 col-start-3 col-span-4">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-body">${item.vendor || ''}</p>
                <h3 class="text-body">${item.quantity}x ${item.title}</h3>
                <p class="text-body">${formattedItemPrice}</p>
                ${item.compare_at_price > item.price ?
                  `<p class="text-body opacity-50" style="text-decoration: line-through;">
                    ${(item.original_price/100).toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                  </p>` : ''
                }
              </div>
              <button class="remove-item underline text-body">
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.54883 3.54883L13.4512 13.4512" stroke="#140B09"/>
                  <path d="M13.4512 3.54883L3.54883 13.4512" stroke="#140B09"/>
                </svg>
              </button>
            </div>

            <div class="flex items-center gap-3 mt-2">
              <div class="quantity-selector ml-auto flex gap-[7px]">
                <button class="quantity-decrease w-7 h-7 border border-warm-black">-</button>
                <input type="number"
                       name="quantity"
                       value="${item.quantity}"
                       min="0"
                       class="w-7 h-7 text-center border border-warm-black bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
                <button class="quantity-increase w-7 h-7 border border-warm-black">+</button>
              </div>
            </div>
          </div>
        </div>
        <div class="border-b border-warm-black"></div>
      `;
      cartItemsContainer.insertAdjacentHTML('beforeend', itemHTML);

      // Add border after each item except the last one

    });

    // Show empty cart message if no items
    if (cart.items.length === 0) {
      cartItemsContainer.innerHTML = `
        <div class="p-4 text-center text-warm-black">
          Your cart is empty
        </div>
      `;
    }
  }
}

// Initialize cart and make it globally accessible
let cartInstance;
document.addEventListener('DOMContentLoaded', () => {
  cartInstance = new Cart();
  window.Cart = cartInstance;

  const productForms = document.querySelectorAll('[data-product-form]')

  // Initialize cart count on page load
  updateCartCount()

  productForms.forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault()

      const submitButton = form.querySelector('button[type="submit"]')
      const originalText = submitButton.innerHTML
      submitButton.innerHTML = 'Adding...'

      try {
        const formData = new FormData(form)
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          body: formData
        })

        if (!response.ok) {
          throw new Error('Network response was not ok')
        }

        submitButton.innerHTML = 'Added!'

        // Update cart count and open drawer
        await updateCartCount()
        // Update cart contents
        await cartInstance.fetchAndUpdateCart()
        toggleCart()

        setTimeout(() => {
          submitButton.innerHTML = originalText
        }, 2000)

      } catch (error) {
        console.error('Error:', error)
        submitButton.innerHTML = 'Error'
        setTimeout(() => {
          submitButton.innerHTML = originalText
        }, 2000)
      }
    })
  })
});

function toggleCart() {
  const cartToggle = document.querySelector('.cart-toggle')
  if (cartToggle) {
    cartToggle.click()
  }
}

async function updateCartCount() {
  try {
    const response = await fetch(window.routes.cart_url + '.js')
    if (!response.ok) throw new Error('Network response was not ok')
    const cart = await response.json()

    // Update all cart count elements
    const cartCountElements = document.querySelectorAll('[data-cart-count]')
    cartCountElements.forEach(element => {
      element.textContent = cart.item_count
      // Hide badge if count is 0
      element.style.display = cart.item_count === 0 ? 'none' : 'flex'
    })

    return cart
  } catch (error) {
    console.error('Error updating cart count:', error)
  }
}
